/**
 * Bombox motion layer
 * --------------------------------------------------------------
 * Native (no library) motion for the Horizon theme:
 *  1. Scroll reveals with stagger (IntersectionObserver + CSS transitions)
 *  2. Hero choreography: eyebrow → headline by words → copy → CTAs; isotype pop + float + pointer parallax
 *  3. Brand confetti: slow-drifting dots behind the page, parallax on scroll
 *  4. Confetti burst when a product is added to the cart (listens to shopify:cart:lines-update)
 *
 * Rules: only transform/opacity are animated; everything is skipped under prefers-reduced-motion;
 * pointer parallax and the ambient layer are desktop-only; the page is fully usable without JS.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');
const PALETTE = ['#F45F5B', '#F5B82E', '#49A1D5', '#66966B'];

const html = document.documentElement;
if (REDUCED.matches) html.classList.remove('bx-js');

const onReady = (fn) =>
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn();

/* ------------------------------------------------------------------ reveals */

const LEAF_SELECTOR = [
  '.text-block',
  '.image-block',
  'a.button',
  'a.button-secondary',
  '.resource-list__item',
  'accordion-custom',
  '.email-signup',
  '.icon-block',
  '.video-block',
  // Bombox sections
  '.bx-section-head > *',
  '.bx-occ__label',
  '.bx-occ__list > li',
  '.bx-tier',
  '.bx-reveal',
  '.bx-spot__media',
  '.bx-spot__content > *',
  '.bx-steps__item',
  '.bx-trust__card',
  '.bx-ship__step',
].join(',');

const isCard = (el) => {
  if (!el.classList.contains('group-block')) return false;
  const cs = getComputedStyle(el);
  const bg = cs.backgroundColor;
  const hasBg = bg && bg !== 'transparent' && !/rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\s*\)/.test(bg);
  const hasBorder = parseFloat(cs.borderTopWidth) > 0 && cs.borderTopStyle !== 'none';
  return hasBg || hasBorder;
};

const collectUnits = (section) => {
  const units = [];
  const cards = [...section.querySelectorAll('.group-block')].filter(isCard);
  const inside = (el) => cards.some((c) => c !== el && c.contains(el));
  cards.forEach((c) => {
    if (!inside(c)) units.push(c);
  });
  section.querySelectorAll(LEAF_SELECTOR).forEach((el) => {
    if (el.closest('.resource-list__item') && !el.classList.contains('resource-list__item')) return;
    if (inside(el)) return;
    if (el.closest('[data-bx-hero]')) return;
    units.push(el);
  });
  return units;
};

const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('bx-in');
      observer.unobserve(entry.target);
    });
  },
  { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
);

const staggerSiblings = (units) => {
  const groups = new Map();
  units.forEach((el) => {
    const key = el.parentElement;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(el);
  });
  groups.forEach((els) => {
    els.forEach((el, i) => {
      el.style.setProperty('--bx-delay', `${Math.min(i * 70, 420)}ms`);
    });
  });
};

const initReveals = (root) => {
  if (REDUCED.matches) return;
  root.querySelectorAll('main .shopify-section, footer .shopify-section').forEach((section) => {
    if (section.dataset.bxReady) return;
    section.dataset.bxReady = '1';
    const units = collectUnits(section).filter((el) => !el.hasAttribute('data-bx-reveal'));
    staggerSiblings(units);
    units.forEach((el) => {
      el.setAttribute('data-bx-reveal', '');
      observer.observe(el);
    });
  });
};

/* --------------------------------------------------------------------- hero */

const splitWords = (h1) => {
  if (h1.dataset.bxSplit) return;
  h1.dataset.bxSplit = '1';
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) return frag.appendChild(document.createTextNode(part));
          const w = document.createElement('span');
          w.className = 'bx-word';
          const inner = document.createElement('span');
          inner.className = 'bx-word__inner';
          inner.textContent = part;
          w.appendChild(inner);
          frag.appendChild(w);
        });
        child.replaceWith(frag);
      } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') {
        walk(child);
      }
    });
  };
  walk(h1);
};

const initHero = () => {
  if (REDUCED.matches) return;
  const main = document.querySelector('main');
  const first = main && main.querySelector('.shopify-section');
  if (!first) return;
  const h1 = first.querySelector('h1');
  if (!h1) return;

  const scope = h1.closest('.section-content-wrapper') || first;
  scope.setAttribute('data-bx-hero', '');

  splitWords(h1);
  let step = 0;
  const seq = [];
  const textCol = h1.closest('.group-block-content') || scope;
  [...textCol.children].forEach((child) => {
    if (child.contains(h1)) {
      child.setAttribute('data-bx-hero-item', '');
      child.style.setProperty('--bx-delay', `${step * 90}ms`);
      h1.querySelectorAll('.bx-word').forEach((w, i) => {
        w.style.setProperty('--bx-delay', `${step * 90 + i * 60}ms`);
      });
      step += 1 + Math.ceil(h1.querySelectorAll('.bx-word').length * 0.6);
      return;
    }
    child.setAttribute('data-bx-hero-item', '');
    child.style.setProperty('--bx-delay', `${step * 90}ms`);
    step += 1;
    seq.push(child);
  });

  const img = scope.querySelector('.image-block img, .image-block');
  if (img) {
    const pop = img.closest('.image-block') || img;
    pop.setAttribute('data-bx-pop', '');
    pop.style.setProperty('--bx-delay', '240ms');
    if (FINE_POINTER.matches) initParallax(pop, scope);
  }

  const reveal = () => scope.classList.add('bx-hero-in');
  requestAnimationFrame(() => requestAnimationFrame(reveal));
  setTimeout(reveal, 400);
};

const initParallax = (el, area) => {
  let tx = 0,
    ty = 0,
    cx = 0,
    cy = 0,
    raf = 0;
  const tick = () => {
    cx += (tx - cx) * 0.08;
    cy += (ty - cy) * 0.08;
    el.style.setProperty('--bx-px', `${cx.toFixed(2)}px`);
    el.style.setProperty('--bx-py', `${cy.toFixed(2)}px`);
    if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) raf = requestAnimationFrame(tick);
    else raf = 0;
  };
  area.addEventListener('pointermove', (e) => {
    const r = area.getBoundingClientRect();
    tx = ((e.clientX - r.left) / r.width - 0.5) * 18;
    ty = ((e.clientY - r.top) / r.height - 0.5) * 14;
    if (!raf) raf = requestAnimationFrame(tick);
  });
  area.addEventListener('pointerleave', () => {
    tx = 0;
    ty = 0;
    if (!raf) raf = requestAnimationFrame(tick);
  });
};

/* ------------------------------------------------------------ ambient dots */

/*
 * Brand confetti behind the page. Three depth layers (small/far dots drift slowly, big/near dots
 * drift faster and parallax more on scroll), a slow upward float with sine sway, and on desktop
 * the dots are gently pushed away by the pointer. Mobile gets fewer dots and a 30fps cap.
 * Stays within the brand rules: solid circles only, few of them, never over the copy (z-index -1).
 */
const initAmbient = () => {
  if (REDUCED.matches) return;
  if (document.querySelector('.bx-ambient')) return;
  const canvas = document.createElement('canvas');
  canvas.className = 'bx-ambient';
  canvas.setAttribute('aria-hidden', 'true');
  document.body.prepend(canvas);
  const ctx = canvas.getContext('2d');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const desktop = FINE_POINTER.matches;
  let w = 0,
    h = 0,
    dots = [],
    running = true;
  const pointer = { x: -9999, y: -9999, active: false };

  const makeDot = (i) => {
    const depth = Math.random(); // 0 far … 1 near
    return {
      x: Math.random() * w,
      y: Math.random() * h,
      r: 2.5 + depth * (desktop ? 6 : 4.5),
      c: PALETTE[i % PALETTE.length],
      depth,
      s: 0.15 + depth * 0.5, // scroll parallax factor
      a: Math.random() * Math.PI * 2,
      v: 0.5 + Math.random() * 0.7, // sway speed
      f: 0.12 + depth * 0.22, // float speed (px/frame, upward)
      ox: 0,
      oy: 0, // pointer offset (eased)
    };
  };

  const resize = () => {
    w = window.innerWidth;
    h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const count = desktop ? Math.round(Math.min(44, Math.max(22, w / 32))) : 16;
    dots = Array.from({ length: count }, (_, i) => makeDot(i));
  };

  if (desktop) {
    window.addEventListener(
      'pointermove',
      (e) => {
        pointer.x = e.clientX;
        pointer.y = e.clientY;
        pointer.active = true;
      },
      { passive: true }
    );
    document.addEventListener('pointerleave', () => (pointer.active = false));
  }

  let last = 0;
  const minFrame = desktop ? 0 : 33;
  const frame = (t) => {
    if (!running) return;
    if (t - last < minFrame) return requestAnimationFrame(frame);
    last = t;
    ctx.clearRect(0, 0, w, h);
    const sy = window.scrollY;
    dots.forEach((d) => {
      d.a += 0.012 * d.v;
      d.y -= d.f; // float up
      if (d.y < -24) {
        d.y = h + 24;
        d.x = Math.random() * w;
      }
      const bx = d.x + Math.sin(d.a) * (10 + d.depth * 10);
      let by = d.y - ((sy * d.s) % (h + 48)) + Math.cos(d.a * 0.7) * 6;
      if (by < -24) by += h + 48;
      if (by > h + 24) by -= h + 48;

      // pointer nudge (desktop): push away within 160px, ease back
      let tx = 0,
        ty = 0;
      if (pointer.active) {
        const dx = bx - pointer.x,
          dy = by - pointer.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 160 && dist > 0.01) {
          const k = ((160 - dist) / 160) * (18 + d.depth * 26);
          tx = (dx / dist) * k;
          ty = (dy / dist) * k;
        }
      }
      d.ox += (tx - d.ox) * 0.1;
      d.oy += (ty - d.oy) * 0.1;

      ctx.globalAlpha = 0.34 + d.depth * 0.26;
      ctx.fillStyle = d.c;
      ctx.beginPath();
      ctx.arc(bx + d.ox, by + d.oy, d.r, 0, Math.PI * 2);
      ctx.fill();
    });
    requestAnimationFrame(frame);
  };

  resize();
  window.addEventListener('resize', resize, { passive: true });
  document.addEventListener('visibilitychange', () => {
    running = !document.hidden;
    if (running) requestAnimationFrame(frame);
  });
  requestAnimationFrame(frame);
};

/* ---------------------------------------------------------- header confetti */
// A handful of small brand dots drifting inside the header band, behind the logo and nav.
const initHeaderConfetti = () => {
  if (REDUCED.matches) return;
  const header = document.querySelector('header-component, #header-component, .header');
  if (!header || header.querySelector('.bx-header-confetti')) return;
  const layer = document.createElement('div');
  layer.className = 'bx-header-confetti';
  layer.setAttribute('aria-hidden', 'true');
  const spots = [
    { x: 3, y: 22, s: 7, c: '#F5B82E', d: 0, t: 10, o: 0.6 },
    { x: 9, y: 68, s: 5, c: '#49A1D5', d: 1.4, t: 12, o: 0.5 },
    { x: 24, y: 14, s: 4, c: '#F45F5B', d: 2.6, t: 9, o: 0.55 },
    { x: 41, y: 78, s: 6, c: '#66966B', d: 0.8, t: 11, o: 0.45 },
    { x: 58, y: 18, s: 5, c: '#F5B82E', d: 3.1, t: 13, o: 0.5 },
    { x: 73, y: 72, s: 4, c: '#F45F5B', d: 1.9, t: 10, o: 0.55 },
    { x: 88, y: 20, s: 7, c: '#49A1D5', d: 0.4, t: 12, o: 0.5 },
    { x: 96, y: 64, s: 5, c: '#66966B', d: 2.2, t: 9, o: 0.55 },
  ];
  layer.innerHTML = spots
    .map(
      (p) =>
        `<span style="--x:${p.x}%;--y:${p.y}%;--s:${p.s}px;--c:${p.c};--d:${p.d}s;--t:${p.t}s;--o:${p.o}"></span>`
    )
    .join('');
  header.prepend(layer);
};

/* ------------------------------------------------------------------- burst */

let lastPointer = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
document.addEventListener(
  'pointerdown',
  (e) => {
    lastPointer = { x: e.clientX, y: e.clientY };
  },
  { capture: true, passive: true }
);

const burst = (x, y) => {
  if (REDUCED.matches) return;
  const canvas = document.createElement('canvas');
  canvas.className = 'bx-burst';
  canvas.setAttribute('aria-hidden', 'true');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = window.innerWidth,
    h = window.innerHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const n = FINE_POINTER.matches ? 70 : 40;
  const parts = Array.from({ length: n }, (_, i) => {
    const ang = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
    const sp = 6 + Math.random() * 9;
    return {
      x,
      y,
      vx: Math.cos(ang) * sp,
      vy: Math.sin(ang) * sp,
      r: 3 + Math.random() * 4,
      c: PALETTE[i % PALETTE.length],
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      shape: i % 3, // 0 circle, 1 rect, 2 triangle-ish
    };
  });
  const start = performance.now();
  const dur = 1300;
  const frame = (t) => {
    const p = (t - start) / dur;
    if (p >= 1) return canvas.remove();
    ctx.clearRect(0, 0, w, h);
    ctx.globalAlpha = p < 0.7 ? 1 : 1 - (p - 0.7) / 0.3;
    parts.forEach((q) => {
      q.vy += 0.32;
      q.vx *= 0.985;
      q.x += q.vx;
      q.y += q.vy;
      q.rot += q.vr;
      ctx.save();
      ctx.translate(q.x, q.y);
      ctx.rotate(q.rot);
      ctx.fillStyle = q.c;
      if (q.shape === 0) {
        ctx.beginPath();
        ctx.arc(0, 0, q.r, 0, Math.PI * 2);
        ctx.fill();
      } else if (q.shape === 1) {
        ctx.fillRect(-q.r, -q.r * 0.6, q.r * 2, q.r * 1.2);
      } else {
        ctx.beginPath();
        ctx.moveTo(0, -q.r);
        ctx.lineTo(q.r, q.r);
        ctx.lineTo(-q.r, q.r);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    });
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
};

document.addEventListener('shopify:cart:lines-update', (e) => {
  if (e && e.action === 'add') burst(lastPointer.x, lastPointer.y);
});

/* ---------------------------------------------------------- section anchors */
// Lets "/#como-funciona" style links reach a section whose DOM id is "shopify-section-template--…__como_funciona".
const initAnchors = () => {
  document.querySelectorAll('main .shopify-section').forEach((section) => {
    const key = section.id.split('__').pop();
    if (!key || document.getElementById(key)) return;
    const anchor = document.createElement('span');
    anchor.id = key.replace(/_/g, '-');
    anchor.className = 'bx-anchor';
    section.prepend(anchor);
  });
  if (location.hash) {
    const target = document.getElementById(location.hash.slice(1));
    if (target) requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
};

/* -------------------------------------------------------------------- boot */

const boot = () => {
  initAnchors();
  initHero();
  initReveals(document);
  initAmbient();
  initHeaderConfetti();

  // Safety nets: if the tab was throttled or an observer never fired, nothing may stay hidden.
  setTimeout(() => {
    document.querySelectorAll('[data-bx-hero]').forEach((s) => s.classList.add('bx-hero-in'));
    document.querySelectorAll('[data-bx-reveal]:not(.bx-in)').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight + 200) el.classList.add('bx-in');
    });
  }, 3500);
  setTimeout(() => {
    document.querySelectorAll('[data-bx-reveal]:not(.bx-in)').forEach((el) => el.classList.add('bx-in'));
  }, 9000);
};

onReady(() => {
  if (document.hidden) {
    document.addEventListener('visibilitychange', () => !document.hidden && boot(), { once: true });
  } else {
    boot();
  }
});

// Theme editor: sections re-render in place.
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target;
  if (section) {
    delete section.dataset.bxReady;
    initReveals(document);
  }
});

export { burst };
